import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnClick } from "../../../library-js/dom-tools/EventListeners";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";
import { EventSlot } from "../../../library-js/events/EventSlot";
import { TemplatesList } from "../../templates/TemplatesList";
import { Rokojori } from "../Rokojori";
import { Overlay } from "./Overlay";
import { OverlayOptions } from "./OverlayOptions";

export class OverlayHintOptions extends OverlayOptions
{
  isCancable:boolean = true;
  readonly onConfirm:EventSlot<any> = new EventSlot();
  readonly onCancel:EventSlot<any> = new EventSlot();

}

export type CONFIRM = "CONFIRM";
export type CANCEL  = "CANCEL";
export type CLOSE   = "CLOSE";

export type OverlayHintResultType = CONFIRM | CANCEL | CLOSE;

export class OverlayHintResult
{
  static readonly CONFIRM:CONFIRM = "CONFIRM";
  static readonly CANCEL:CANCEL = "CANCEL";
  static readonly CLOSE:CLOSE = "CLOSE";

  action:OverlayHintResultType;

  get confirmed()
  {
    return this.action === OverlayHintResult.CONFIRM;
  }

  get cancelled()
  {
    return this.action === OverlayHintResult.CANCEL;
  }

  get closed()
  {
    return this.action === OverlayHintResult.CLOSE;
  }
}

export class OverlayHint
{
  static readonly title   = new ElementType( "overlay-hint-title" );
  static readonly content = new ElementType( "overlay-hint-content" );
  static readonly buttons = new ElementType( "overlay-hint-buttons" );

  static readonly confirm = new ClassFlag( "confirm" );
  static readonly cancel = new ClassFlag( "cancel" );
  
  static async hint( title:Node|string, content:Node|string, options:OverlayHintOptions = null, hintCustomizer:(e:Element)=>void = null ):Promise<OverlayHintResult>
  {
    if ( typeof title === "string" )
    {
      title = document.createTextNode( title );
    }
    
    if ( typeof content === "string" )
    {
      content = document.createTextNode( content );
    }

    let passedOptions = options;

    let createdOptions = new OverlayHintOptions();
    createdOptions.isCancable = passedOptions ? passedOptions.isCancable : true;
    createdOptions.isClosable = passedOptions ? passedOptions.isClosable : true;    

    let overlayHint = Rokojori.$.templatesManager.createTemplate( TemplatesList.OVERLAY_HINT );    
    OverlayHint.title.query( overlayHint ).appendChild( title );
    OverlayHint.content.query( overlayHint ).appendChild( content );

    let buttonsContainer = OverlayHint.buttons.query( overlayHint );
    
    let confirmButtons = OverlayHint.confirm.queryAll( buttonsContainer );
    let cancelButtons  = OverlayHint.cancel.queryAll( buttonsContainer );
    
    confirmButtons.forEach( 
      c =>
      {
        OnClick.add( c as HTMLElement, ()=> createdOptions.onConfirm.dispatch( null )  );
      }
      
    );

    if ( createdOptions.isCancable )
    {
      cancelButtons.forEach(  
        c => 
        {
          OnClick.add( c as HTMLElement, ()=> createdOptions.onCancel.dispatch( null )  );
        }
      );
    }
    else
    {
      cancelButtons.forEach(  c => HTMLEditor.remove( c ) );
    }
    

    if ( hintCustomizer ) 
    {
      hintCustomizer( overlayHint );
    }
    
    let wasResolved = false;

    let promise = new Promise<OverlayHintResult>(
      ( resolve, reject )=>
      {
        createdOptions.onConfirm.addListener(
          ()=>
          {
            if ( wasResolved ){ return; }
            wasResolved = true;

            Overlay.hide();

            let result = new OverlayHintResult();
            result.action = OverlayHintResult.CONFIRM;

            if ( passedOptions )
            {
              passedOptions.onConfirm.dispatch( null );
            }
            resolve( result );
          }
        );

        createdOptions.onCancel.addListener(
          ()=>
          {
            if ( wasResolved ){ return; }
            wasResolved = true;

            Overlay.hide();

            let result = new OverlayHintResult();
            result.action = OverlayHintResult.CANCEL;

            if ( passedOptions )
            {
              passedOptions.onCancel.dispatch( null );
            }


            resolve( result );
          }
        );


        createdOptions.onClose.addListener(
          ()=>
          {
            if ( wasResolved ){ return; }
            wasResolved = true;
            let result = new OverlayHintResult();
            result.action = OverlayHintResult.CLOSE;

            if ( passedOptions )
            {
              passedOptions.onClose.dispatch( null );
            }

            resolve( result );
          }
        );

        
      }
    );

    Overlay.show( overlayHint as HTMLElement, createdOptions );

    return promise;
    
  }
}